import { useState, useEffect } from 'react';
import axios from 'axios';
import './EmailStudio.css';
import Menu from '../Shared/Menu';
import usePageTitle from '../Shared/usePageTitle';

const API_URL = '/rest/api/v1/email-studio';

/* ─── Static Fallback Data ─── */
const STATIC_EMAILS = [
    {
        id: 'em-1', name: 'Welcome Email 1', status: 'Active',
        subject: 'Welcome to ACME \u2014 let\u2019s get you started \ud83c\udf89',
        journey: 'Welcome Series', lastSent: 'Today, 9:41 AM',
        sent: 142800, delivered: 140100, openRate: 43.7, ctor: 47.2, unsubs: 0.3,
        blocks: 6, dynamic: 1,
    },
    {
        id: 'em-2', name: 'Day 3 \u2014 Nurture', status: 'Active',
        subject: '3 things you can do with ACME right now',
        journey: 'Welcome Series', lastSent: 'Today, 9:43 AM',
        sent: 118500, delivered: 116300, openRate: 38.3, ctor: 43.1, unsubs: 0.2,
        blocks: 6, dynamic: 2,
    },
    {
        id: 'em-3', name: 'Week 1 \u2014 Offer', status: 'Active',
        subject: 'Here\u2019s a little something for your first week \u2728',
        journey: 'Welcome Series', lastSent: 'Today, 9:46 AM',
        sent: 94200, delivered: 92700, openRate: 42.0, ctor: 45.8, unsubs: 0.2,
        blocks: 7, dynamic: 2,
    },
    {
        id: 'em-4', name: 'We Miss You', status: 'Active',
        subject: 'It\u2019s been a while, {{FirstName}} \u2014 we\u2019ve missed you',
        journey: 'Re-engagement Campaign', lastSent: 'Mar 7, 6:05 AM',
        sent: 58400, delivered: 57200, openRate: 32.6, ctor: 36.7, unsubs: 2.1,
        blocks: 6, dynamic: 1,
    },
    {
        id: 'em-5', name: 'Last Chance Offer', status: 'Active',
        subject: 'Last chance \u2014 your offer expires at midnight',
        journey: 'Re-engagement Campaign', lastSent: 'Mar 7, 6:08 AM',
        sent: 31800, delivered: 31200, openRate: 45.0, ctor: 55.7, unsubs: 0.9,
        blocks: 7, dynamic: 2,
    },
    {
        id: 'em-6', name: 'Thank You \u2014 Receipt', status: 'Paused',
        subject: 'Your order #{{OrderID}} is confirmed \u2713',
        journey: 'Post-Purchase Follow Up', lastSent: 'Jan 14, 2:05 AM',
        sent: 203400, delivered: 201400, openRate: 73.8, ctor: 56.7, unsubs: 0.1,
        blocks: 7, dynamic: 2,
    },
    {
        id: 'em-7', name: 'Review Request', status: 'Paused',
        subject: 'How was your experience, {{FirstName}}?',
        journey: 'Post-Purchase Follow Up', lastSent: 'Jan 14, 2:07 AM',
        sent: 198100, delivered: 196200, openRate: 45.6, ctor: 38.3, unsubs: 0.0,
        blocks: 5, dynamic: 1,
    },
    {
        id: 'em-8', name: 'Upsell Recommendation', status: 'Paused',
        subject: 'Based on your order \u2014 you might love these',
        journey: 'Post-Purchase Follow Up', lastSent: 'Jan 14, 2:09 AM',
        sent: 182800, delivered: 180200, openRate: 34.7, ctor: 45.1, unsubs: 0.1,
        blocks: 6, dynamic: 1,
    },
    {
        id: 'em-9', name: 'Happy Birthday!', status: 'Active',
        subject: 'Happy Birthday {{FirstName}} \ud83c\udf82 \u2014 your gift is inside',
        journey: 'Birthday Journey', lastSent: 'Today, 12:05 AM',
        sent: 31200, delivered: 30900, openRate: 80.0, ctor: 74.5, unsubs: 0.1,
        blocks: 6, dynamic: 2,
    },
    {
        id: 'em-10', name: 'Birthday Offer Reminder', status: 'Active',
        subject: 'Your birthday offer expires in 48 hours \u23f0',
        journey: 'Birthday Journey', lastSent: 'Today, 12:07 AM',
        sent: 22400, delivered: 22200, openRate: 53.4, ctor: 61.2, unsubs: 0.2,
        blocks: 5, dynamic: 1,
    },
];

function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return 'Today, ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function normalizeEmail(item) {
    const sent      = item.sent || item.emailSent || item.numSent || 0;
    const delivered = item.delivered || item.numDelivered || 0;
    const opened    = item.opened || item.uniqueOpens || item.numOpens || 0;
    const clicked   = item.clicked || item.uniqueClicks || item.numClicks || 0;
    const openRate  = item.openRate != null ? parseFloat(Number(item.openRate).toFixed(1))
                       : (delivered > 0 && opened > 0 ? parseFloat(((opened / delivered) * 100).toFixed(1)) : 0);
    const ctorVal   = item.ctor != null ? parseFloat(Number(item.ctor).toFixed(1))
                       : (opened > 0 && clicked > 0 ? parseFloat(((clicked / opened) * 100).toFixed(1)) : 0);
    return {
        ...item,
        id:         item.id,
        name:       item.name || 'Unnamed Email',
        status:     item.status || 'Draft',
        subject:    item.subject || '',
        preHeader:  item.preHeader || item.preheader || item.preHeaderText || '',
        journey:    item.journey || '',
        lastSent:   item.lastSent ? formatDate(item.lastSent) : '',
        sent,
        delivered,
        openRate,
        ctor:       ctorVal,
        unsubs:     item.unsubs || item.unsubRate || (sent > 0 ? parseFloat(((item.unsubscribed || item.numUnsubscribes || 0) / sent * 100).toFixed(1)) : 0),
        blocks:     item.blocks || item.contentBlockCount || 0,
        dynamic:    item.dynamic || item.dynamicBlockCount || 0,
        fromName:   item.fromName || item.ownerName || item.senderName || '',
        fromEmail:  item.fromEmail || item.ownerEmail || item.senderEmail || '',
        replyTo:    item.replyTo || item.replyToEmail || item.ownerEmail || '',
        type:       item.type || item.emailType || '',
        template:   item.template || item.templateName || '',
        owner:      item.owner || item.sender || null,
    };
}

/* ─── Helpers ─── */
function formatNumber(num) {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return String(num);
}

function metricColor(val) {
    if (val >= 40) return '#10B981';
    if (val >= 25) return '#F59E0B';
    return '#EF4444';
}

/* ─── Email Detail Panel (slideout) ─── */
function EmailDetailPanel({ email, onClose }) {
    const [activeTab, setActiveTab] = useState('overview');
    if (!email) return null;

    const sent      = email.sent || 0;
    const delivered = email.delivered || 0;
    const opened    = delivered > 0 ? Math.round(delivered * (email.openRate || 0) / 100) : 0;
    const clicked   = opened > 0 ? Math.round(opened * (email.ctor || 0) / 100) : 0;
    const bounced   = sent - delivered;
    const unsubs    = sent > 0 ? Math.round(sent * (email.unsubs || 0) / 100) : 0;
    const delivPct  = sent > 0 ? ((delivered / sent) * 100).toFixed(1) : 0;
    const openPct   = email.openRate || 0;
    const ctorPct   = email.ctor || 0;
    const bouncePct = sent > 0 ? ((bounced / sent) * 100).toFixed(1) : 0;
    const unsubPct  = email.unsubs || 0;

    function FunnelBar({ label, value, percent, color }) {
        return (
            <div style={{ marginBottom: 16 }}>
                <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#64748B' }}>{label}</span>
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ fontWeight: 700, fontSize: 14, color: '#0F172A' }}>{formatNumber(value)}</span>
                        <span style={{ fontSize: 12, color: '#94A3B8' }}>{percent}%</span>
                    </div>
                </div>
                <div style={{ width: '100%', height: 8, background: '#E2E8F0', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${percent}%`, height: '100%', background: color }} />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="step-panel-backdrop show" onClick={onClose} />
            <div className="step-panel show" style={{ width: 560 }}>
                <div style={{ padding: '24px 28px', borderBottom: '1px solid #E2E8F0', background: '#fff' }}>
                    <div className="d-flex align-items-start justify-content-between w-100">
                        <div className="d-flex align-items-start gap-3">
                            <div style={{ width: 48, height: 48, background: '#D1FAE5', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>📧</div>
                            <div>
                                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                                    <span style={{ padding: '4px 10px', background: email.status === 'Active' ? '#ECFDF5' : '#FFFBEB', color: email.status === 'Active' ? '#059669' : '#D97706', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>● {email.status}</span>
                                    {email.journey && <span style={{ padding: '4px 10px', background: '#F3E8FF', color: '#9333EA', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{email.journey}</span>}
                                </div>
                                <h2 style={{ fontWeight: 800, fontSize: 20, color: '#0F172A', margin: 0 }}>{email.name}</h2>
                            </div>
                        </div>
                        <button className="step-panel-close" onClick={onClose}>✕</button>
                    </div>
                    <div className="d-flex gap-4 w-100" style={{ marginTop: 16 }}>
                        <div>
                            <p style={{ fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, fontWeight: 600 }}>SENT</p>
                            <p style={{ fontWeight: 800, fontSize: 24, color: '#0F172A', margin: 0 }}>{formatNumber(sent)}</p>
                        </div>
                        <div>
                            <p style={{ fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, fontWeight: 600 }}>OPEN RATE</p>
                            <p style={{ fontWeight: 800, fontSize: 24, color: metricColor(openPct), margin: 0 }}>{openPct}%</p>
                        </div>
                        <div>
                            <p style={{ fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, fontWeight: 600 }}>CTOR</p>
                            <p style={{ fontWeight: 800, fontSize: 24, color: metricColor(ctorPct), margin: 0 }}>{ctorPct}%</p>
                        </div>
                    </div>
                </div>
                <div className="step-panel-tabs d-flex" style={{ background: '#FAFBFC' }}>
                    <button className={`modal-tab${activeTab === 'overview' ? ' active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
                    <button className={`modal-tab${activeTab === 'performance' ? ' active' : ''}`} onClick={() => setActiveTab('performance')}>Performance</button>
                </div>
                <div style={{ padding: 28, background: '#FAFBFC' }}>
                    {activeTab === 'overview' && (
                        <>
                            {/* Subject Line */}
                            {email.subject && (
                                <div style={{ marginBottom: 24 }}>
                                    <p style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>SUBJECT LINE</p>
                                    <p style={{ fontSize: 15, color: '#0F172A', fontWeight: 600, margin: 0 }}>{email.subject}</p>
                                </div>
                            )}

                            {/* Preheader */}
                            {email.preHeader && (
                                <div style={{ marginBottom: 28 }}>
                                    <p style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>PREHEADER</p>
                                    <p style={{ fontSize: 14, color: '#475569', margin: 0 }}>{email.preHeader}</p>
                                </div>
                            )}

                            {/* Sender Details */}
                            <div style={{ marginBottom: 28 }}>
                                <p style={{ fontSize: 12, fontWeight: 700, color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>SENDER DETAILS</p>
                                <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 16 }}>
                                    {[
                                        { label: 'From Name', value: email.fromName || email.ownerName || '—' },
                                        { label: 'From Email', value: email.fromEmail || email.ownerEmail || '—' },
                                        { label: 'Reply-To', value: email.replyTo || email.ownerEmail || '—' },
                                    ].map((r, i) => (
                                        <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', ...(i > 0 ? { borderTop: '1px solid #F1F5F9' } : {}) }}>
                                            <span style={{ fontSize: 13, color: '#64748B' }}>{r.label}</span>
                                            <span style={{ fontSize: 13, color: '#0F172A', fontWeight: 600, fontFamily: "'Courier New', monospace" }}>{r.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Details */}
                            <div>
                                <p style={{ fontSize: 12, fontWeight: 700, color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>DETAILS</p>
                                <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 16 }}>
                                    {[
                                        { label: 'Status', value: email.status || '—' },
                                        { label: 'Journey', value: email.journey || '—' },
                                        { label: 'Last Sent', value: email.lastSent || '—' },
                                        { label: 'Type', value: email.type || email.emailType || '—' },
                                        ...(email.template ? [{ label: 'Template', value: email.template }] : []),
                                    ].map((r, i) => (
                                        <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', ...(i > 0 ? { borderTop: '1px solid #F1F5F9' } : {}) }}>
                                            <span style={{ fontSize: 13, color: '#64748B' }}>{r.label}</span>
                                            <span style={{ fontSize: 13, color: '#0F172A', fontWeight: 600 }}>{r.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                    {activeTab === 'performance' && (
                        <>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
                                {[{ label: 'OPEN RATE', value: `${openPct}%`, sub: `${formatNumber(opened)} opens of ${formatNumber(delivered)} delivered`, color: metricColor(openPct) },
                                  { label: 'CTOR', value: `${ctorPct}%`, sub: `${formatNumber(clicked)} clicks of ${formatNumber(opened)} opens`, color: metricColor(ctorPct) },
                                  { label: 'BOUNCE RATE', value: `${bouncePct}%`, sub: `${formatNumber(bounced)} bounced`, color: '#64748B' },
                                  { label: 'UNSUB RATE', value: `${unsubPct}%`, sub: `${formatNumber(unsubs)} unsubscribed`, color: '#EF4444' },
                                ].map(k => (
                                    <div key={k.label} style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 20 }}>
                                        <p style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>{k.label}</p>
                                        <p style={{ fontWeight: 800, fontSize: 32, color: k.color, margin: '0 0 6px 0' }}>{k.value}</p>
                                        <p style={{ fontSize: 12, color: '#94A3B8', margin: 0 }}>{k.sub}</p>
                                    </div>
                                ))}
                            </div>
                            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 24 }}>
                                <p style={{ fontSize: 12, fontWeight: 700, color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 20 }}>DELIVERY FUNNEL</p>
                                <FunnelBar label="SENT" value={sent} percent="100.0" color="#6366F1" />
                                <FunnelBar label="DELIVERED" value={delivered} percent={delivPct} color="#3B82F6" />
                                <FunnelBar label="OPENED" value={opened} percent={openPct} color="#10B981" />
                                <FunnelBar label="CLICKED" value={clicked} percent={ctorPct} color="#8B5CF6" />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

/* ─── Email Row Card ─── */
function EmailRowCard({ email, onClick }) {
    const isActive = email.status === 'Active';
    const accentColor = isActive ? '#10B981' : '#F59E0B';
    const badgeBg    = isActive ? '#ECFDF5' : '#FFFBEB';
    const badgeColor = isActive ? '#059669' : '#D97706';
    const badgeBorder = isActive ? '#10B98140' : '#F59E0B40';

    return (
        <div className="email-row-card" onClick={onClick} style={{ cursor: 'pointer' }}>
            <div className="email-accent-stripe" style={{ background: accentColor }} />
            <div className="email-card-content">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                            <h3 className="email-card-title">{email.name}</h3>
                            <span className="email-status-badge" style={{ background: badgeBg, color: badgeColor, borderColor: badgeBorder }}>
                                <span className="status-dot-active" style={{ background: accentColor }} />
                                {email.status}
                            </span>
                            <span className="email-type-badge">Journey</span>
                        </div>
                        <p className="email-meta-text">
                            {email.journey} &middot; Last sent {email.lastSent}
                        </p>
                    </div>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" className="email-chevron">
                        <path d="M9 18l6-6-6-6" />
                    </svg>
                </div>

                {/* Subject */}
                <p className="email-subject">&ldquo;{email.subject}&rdquo;</p>

                {/* Metrics + Blocks */}
                <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
                    <div className="email-metrics-row">
                        <div className="email-metric-item">
                            <p className="es-metric-label">Sent</p>
                            <p className="es-metric-value">{formatNumber(email.sent)}</p>
                        </div>
                        <div className="email-metric-item">
                            <p className="es-metric-label">Delivered</p>
                            <p className="es-metric-value" style={{ color: '#3B82F6' }}>{formatNumber(email.delivered)}</p>
                        </div>
                        <div className="email-metric-item">
                            <p className="es-metric-label">Open Rate</p>
                            <p className="es-metric-value" style={{ color: metricColor(email.openRate) }}>{email.openRate}%</p>
                        </div>
                        <div className="email-metric-item">
                            <p className="es-metric-label">CTOR</p>
                            <p className="es-metric-value" style={{ color: metricColor(email.ctor) }}>{email.ctor}%</p>
                        </div>
                        <div className="email-metric-item">
                            <p className="es-metric-label">Unsubs</p>
                            <p className="es-metric-value" style={{ color: '#EF4444' }}>{email.unsubs}%</p>
                        </div>
                    </div>

                    <div className="email-blocks-pills">
                        <span className="email-blocks-badge">{email.blocks} blocks</span>
                        <span className="email-dynamic-badge">{email.dynamic} dynamic</span>
                    </div>
                </div>

                {/* Progress bar */}
                <div className="progress-bar-custom" style={{ height: 4 }}>
                    <div className="progress-bar-fill" style={{ width: `${email.openRate}%`, background: '#10B981' }} />
                </div>
            </div>
        </div>
    );
}

/* ─── Main Page ─── */
function EmailStudio() {
    usePageTitle('Email Studio');
    const [allEmails,     setAllEmails]     = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [error,         setError]         = useState(null);
    const [searchQuery,   setSearchQuery]   = useState('');
    const [journeyFilter, setJourneyFilter] = useState('All Journeys');
    const [statusFilter,  setStatusFilter]  = useState('All');
    const [selectedEmail, setSelectedEmail] = useState(null);

    useEffect(() => {
        axios.get(API_URL)
            .then(res => {
                const data = res.data;
                const list = Array.isArray(data) ? data : (data.items || data.emails || data.data || []);
                setAllEmails(list.map(normalizeEmail));
            })
            .catch(err => setError(err.response?.data?.message || err.message))
            .finally(() => setLoading(false));
    }, []);

    const EMAILS = allEmails;

    const filtered = EMAILS.filter(em => {
        if (journeyFilter !== 'All Journeys' && em.journey !== journeyFilter) return false;
        if (statusFilter !== 'All' && em.status !== statusFilter) return false;
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            return (em.name && em.name.toLowerCase().includes(q))
                || (em.subject && em.subject.toLowerCase().includes(q))
                || (em.journey && em.journey.toLowerCase().includes(q));
        }
        return true;
    });

    // Dynamic journeys from data
    const journeySet = new Set(EMAILS.map(e => e.journey).filter(Boolean));
    const JOURNEYS   = ['All Journeys', ...Array.from(journeySet)];

    // KPIs
    const totalEmails  = EMAILS.length;
    const totalSent    = EMAILS.reduce((s, e) => s + (e.sent || 0), 0);
    const avgOpenRate  = totalEmails > 0 ? (EMAILS.reduce((s, e) => s + (e.openRate || 0), 0) / totalEmails).toFixed(1) : '0';
    const totalBlocks  = EMAILS.reduce((s, e) => s + (e.blocks || 0), 0);
    const dynamicBlocks = EMAILS.reduce((s, e) => s + (e.dynamic || 0), 0);

    return (
        <>
            <Menu />

            <div className="container fade-up container-main">
                <div className="page-header mb-4">
                    <h1>Email Studio</h1>
                    <p>All emails active in journeys — with content blocks, performance metrics and sender details</p>
                </div>

                {loading && <div className="text-center py-5"><p>Loading emails...</p></div>}
                {error && <div className="text-center py-5"><p className="text-danger">Failed to load: {error}</p></div>}

                {!loading && !error && <>
                {/* KPI Tiles */}
                <div className="row g-3 mb-4">
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon" style={{ background: '#6366F114' }}>&#x1F4E7;</div>
                            <div>
                                <p className="kpi-label">Total Emails</p>
                                <p className="kpi-value">{totalEmails}</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon" style={{ background: '#0EA5E914' }}>&#x25C8;</div>
                            <div>
                                <p className="kpi-label">Total Sent</p>
                                <p className="kpi-value">{formatNumber(totalSent)}</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon" style={{ background: '#10B98114' }}>&#x2197;</div>
                            <div>
                                <p className="kpi-label">Avg Open Rate</p>
                                <p className="kpi-value">{avgOpenRate}%</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon" style={{ background: '#8B5CF614' }}>&#x2B21;</div>
                            <div>
                                <p className="kpi-label">Content Blocks</p>
                                <p className="kpi-value">{totalBlocks}</p>
                                <p className="kpi-subtext">{dynamicBlocks} dynamic</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="d-flex gap-3 mb-3 flex-wrap align-items-center">
                    <div className="search-wrapper flex-grow-1">
                        <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search by name, subject or journey..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="filter-buttons">
                        {JOURNEYS.map(j => (
                            <button
                                key={j}
                                className={`filter-btn${journeyFilter === j ? ' active' : ''}`}
                                onClick={() => setJourneyFilter(j)}
                            >
                                {j}
                            </button>
                        ))}
                    </div>
                    <div className="filter-buttons">
                        {['All', 'Active', 'Paused'].map(s => (
                            <button
                                key={s}
                                className={`filter-btn${statusFilter === s ? ' active' : ''}`}
                                onClick={() => setStatusFilter(s)}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Results count */}
                <p className="de-results-count mb-3">
                    Showing <strong>{filtered.length}</strong> of {EMAILS.length} emails
                </p>

                {/* Email Cards */}
                {filtered.length > 0 ? (
                    <div className="d-flex flex-column gap-3">
                        {filtered.map(em => (
                            <EmailRowCard key={em.id} email={em} onClick={() => setSelectedEmail(em)} />
                        ))}
                    </div>
                ) : (
                    <div className="empty-state">
                        <div className="empty-state-icon">🔭</div>
                        <h3 className="empty-state-title">No emails found</h3>
                        <p className="empty-state-text">No emails match your filters.</p>
                    </div>
                )}
                </>}
            </div>

            {selectedEmail && <EmailDetailPanel email={selectedEmail} onClose={() => setSelectedEmail(null)} />}
        </>
    );
}

export default EmailStudio;
